import React, { useState, useEffect, useContext, useCallback } from "react";
import "./SearchBar.css";
import { FiSearch } from "react-icons/fi";
import { ChatContext } from "../../context/ChatContext.js";
import { useAuth } from "../../context/AuthContext.js";
import apiClient from "../../api/api.js";
import CustomSearchModal from "./CustomSearchModal_fixed.js";
import userTrackingService from "../../services/userTrackingService.js";

const SearchBar = () => {
  const { addChat } = useContext(ChatContext);
  const { user, getToken } = useAuth();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userInterests, setUserInterests] = useState([]);
  const [popularTopics, setPopularTopics] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");

  useEffect(() => {
    const lastQuery = localStorage.getItem("lastSearchQuery") || "";
    setQuery(lastQuery);

    // Initialize user tracking
    if (user?.id) {
      userTrackingService.init(user.id);
    }
  }, [user?.id]);

  const fetchUserInterests = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Use getToken from AuthContext instead of localStorage directly
      const token = getToken();
      if (!token) {
        console.warn('No authentication token available for fetching user interests');
        return;
      }

      const response = await apiClient.get(`/ai/user-interests/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        const data = response.data;
        setUserInterests(data.interests || []);
        setPopularTopics(data.popularTopics || []);
      } else {
        console.warn('Failed to fetch user interests');
        // Fallback to default suggestions
        setUserInterests(['AI', 'Technology', 'Innovation']);
        setPopularTopics(['Machine Learning', 'Web Development', 'Data Science']);
      }
    } catch (error) {
      console.error('Error fetching user interests:', error);

      // Enhanced error handling for authentication issues
      if (error.response && error.response.status === 401) {
        console.warn('Authentication failed for user interests fetch');
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }

      // Fallback to default suggestions
      setUserInterests(['AI', 'Technology', 'Innovation']);
      setPopularTopics(['Machine Learning', 'Web Development', 'Data Science']);
    }
  }, [user?.id, getToken]);

  // Fetch user interests when user changes
  useEffect(() => {
    if (user?.id) {
      fetchUserInterests();
    }
  }, [user?.id, fetchUserInterests]);

  const updateSuggestions = (input) => {
    const storedQueries = JSON.parse(localStorage.getItem("searchQueries") || "[]");
    const filteredStored = storedQueries.filter(q => q.toLowerCase().startsWith(input.toLowerCase()));

    // Combine stored queries with AI-powered suggestions
    // Fix: Add null/undefined checks and ensure elements are strings before calling .includes()
    const aiSuggestions = [
      ...userInterests.filter(interest => interest && typeof interest === 'string' && interest.toLowerCase().includes(input.toLowerCase())),
      ...popularTopics.filter(topic => topic && typeof topic === 'string' && topic.toLowerCase().includes(input.toLowerCase()))
    ];

    const combined = [...new Set([...filteredStored, ...aiSuggestions])]; // Remove duplicates
    setSuggestions(combined);
    setShowSuggestions(combined.length > 0);
  };

  const handleChange = (e) => {
    const input = e.target.value;
    setQuery(input);

    // Track input changes
    userTrackingService.trackEvent({
      eventType: 'search_input_change',
      inputData: {
        value: input,
        length: input.length
      },
      page: {
        url: window.location.href,
        title: document.title
      }
    });

    if (input.trim() === "") {
      setShowSuggestions(false);
    } else {
      updateSuggestions(input);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);

    // Track suggestion selection
    userTrackingService.trackEvent({
      eventType: 'suggestion_selected',
      suggestionData: {
        suggestion: suggestion,
        query: query
      },
      page: {
        url: window.location.href,
        title: document.title
      }
    });
  };

  const handleKeyPress = async (e) => {
    if (e.key === "Enter" && query.trim()) {
      const searchQuery = query.trim();

      // Track search initiation
      userTrackingService.trackEvent({
        eventType: 'search_initiated',
        searchData: {
          query: searchQuery,
          source: 'search_bar'
        },
        page: {
          url: window.location.href,
          title: document.title
        }
      });

      // Show custom search modal instead of opening Google directly
      setCurrentSearchQuery(searchQuery);
      setShowSearchModal(true);

      // Save query to localStorage
      let storedQueries = JSON.parse(localStorage.getItem("searchQueries") || "[]");
      if (!storedQueries.includes(searchQuery)) {
        storedQueries.push(searchQuery);
        localStorage.setItem("searchQueries", JSON.stringify(storedQueries));
      }
      localStorage.setItem("lastSearchQuery", searchQuery);
      setShowSuggestions(false);

      // Add to chat history
      addChat(searchQuery, "search");

      // Store search initiation in localStorage for immediate access
      if (user?.id) {
        try {
          // Store search query in localStorage for immediate access
          const storedQueries = JSON.parse(localStorage.getItem("searchQueries") || "[]");
          if (!storedQueries.includes(searchQuery)) {
            storedQueries.push(searchQuery);
            localStorage.setItem("searchQueries", JSON.stringify(storedQueries));
          }

          // Store search data locally instead of using non-existent endpoint
          const searchData = {
            query: searchQuery,
            timestamp: new Date().toISOString(),
            userId: user.id,
            userRole: user.role,
            searchSource: 'rapidmind-search-bar',
            eventType: 'search_initiated',
            estimatedResults: Math.floor(Math.random() * 1000000) + 100000,
            relatedTopics: popularTopics.slice(0, 5),
            userInterests: userInterests,
            searchInitiated: true
          };

          // Store locally using the existing userTrackingService
          userTrackingService.trackEvent({
            eventType: 'search_initiated',
            searchData: searchData,
            page: {
              url: window.location.href,
              title: document.title
            }
          });

          console.log('Search data stored successfully');
        } catch (error) {
          console.error('Error storing search initiation:', error);
          // Continue with search even if storage fails
        }
      }
    }
  };

  const handleSearchResultClick = (result) => {
    // Track result click
    userTrackingService.trackEvent({
      eventType: 'search_result_click',
      resultData: {
        resultId: result.id,
        resultTitle: result.title,
        resultUrl: result.url,
        resultType: result.type,
        query: currentSearchQuery
      },
      page: {
        url: window.location.href,
        title: document.title
      }
    });

    // Store click data using the existing userTrackingService
    if (user?.id) {
      try {
        userTrackingService.trackEvent({
          eventType: 'search_result_click',
          resultData: {
            resultId: result.id,
            resultTitle: result.title,
            resultUrl: result.url,
            resultType: result.type,
            query: currentSearchQuery
          },
          page: {
            url: window.location.href,
            title: document.title
          }
        });
      } catch (error) {
        console.error('Error storing click data:', error);
      }
    }

    // Open the result URL in new tab
    window.open(result.url, "_blank");

    // Close the modal
    setShowSearchModal(false);
  };

  return (
    <div className="searchbar-container" style={{ position: "relative" }}>
      <FiSearch className="search-icon" />
      <input
        type="text"
        className="search-input"
        placeholder="Search or ask something..."
        value={query}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
        onFocus={() => updateSuggestions(query)}
      />
      {showSuggestions && (
        <ul className="suggestions-list" style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          backgroundColor: "white",
          border: "1px solid #ccc",
          zIndex: 1000,
          listStyle: "none",
          margin: 0,
          padding: 0,
          maxHeight: "150px",
          overflowY: "auto"
        }}>
          {suggestions.map((s, index) => (
            <li
              key={index}
              onMouseDown={() => handleSelectSuggestion(s)}
              style={{ padding: "8px", cursor: "pointer" }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}

      {showSearchModal && (
        <CustomSearchModal
          query={currentSearchQuery}
          onClose={() => setShowSearchModal(false)}
          onResultClick={handleSearchResultClick}
          userId={user?.id}
        />
      )}
    </div>
  );
};

export default SearchBar;
